/**
 * Compact Stat Card
 *
 * A small, information-dense card for displaying a single metric.
 * Designed for the Trading Analysis dashboard with proper proportions.
 */

import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

interface StatCardProps {
  /** Label for the metric */
  label: string;
  /** Main value to display */
  value: string | number;
  /** Optional secondary value (e.g., change) */
  subValue?: string;
  /** Color variant */
  variant?: "default" | "positive" | "negative" | "neutral";
  /** Optional icon */
  icon?: ReactNode;
  /** Additional className */
  className?: string;
  /** Optional tooltip content - shows dashed underline when provided */
  tooltip?: string;
}

export function StatCard({
  label,
  value,
  subValue,
  variant = "default",
  icon,
  className,
  tooltip,
}: StatCardProps) {
  const valueColorClass = {
    default: "text-foreground",
    positive: "text-bid-foreground",
    negative: "text-ask-foreground",
    neutral: "text-muted-foreground",
  }[variant];

  const labelElement = (
    <span 
      className={cn(
        "text-xs text-muted-foreground truncate",
        tooltip && "border-b border-dashed border-muted-foreground/50 cursor-default select-none"
      )}
    >
      {label}
    </span>
  );

  return (
    <div
      className={cn(
        "rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/40",
        className
      )}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {icon && (
          <span className="text-muted-foreground flex-shrink-0">{icon}</span>
        )}
        {tooltip ? (
          <TooltipPrimitive.Provider delayDuration={250}>
            <TooltipPrimitive.Root>
              <TooltipPrimitive.Trigger asChild>
                {labelElement}
              </TooltipPrimitive.Trigger>
              <TooltipPrimitive.Portal>
                <TooltipPrimitive.Content
                  side="top"
                  sideOffset={5}
                  className={cn(
                    "z-50 max-w-[280px] rounded-md px-3 py-2",
                    "bg-card/95 text-foreground/90 backdrop-blur-sm",
                    "border border-border dark:border-muted-foreground/20 shadow-lg",
                    "animate-in fade-in-0 zoom-in-95",
                    "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
                    "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
                  )}
                >
                  <p className="text-xs leading-relaxed">{tooltip}</p>
                </TooltipPrimitive.Content>
              </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
          </TooltipPrimitive.Provider>
        ) : (
          labelElement
        )}
      </div>
      <div className={cn("text-lg font-bold leading-tight", valueColorClass)}>
        {value}
      </div>
      {subValue && (
        <div className="text-[10px] text-muted-foreground mt-0.5">
          {subValue}
        </div>
      )}
    </div>
  );
}

/**
 * Inline stat for compact displays
 */
interface InlineStatProps {
  label: string;
  value: string | number;
  variant?: "default" | "positive" | "negative";
  className?: string;
}

export function InlineStat({
  label,
  value,
  variant = "default",
  className,
}: InlineStatProps) {
  const valueColorClass = {
    default: "text-foreground",
    positive: "text-bid-foreground",
    negative: "text-ask-foreground",
  }[variant];

  return (
    <div
      className={cn("flex items-center justify-between py-1.5 px-2", className)}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn("text-sm font-semibold", valueColorClass)}>
        {value}
      </span>
    </div>
  );
}

/**
 * Hero stat for large prominent display
 */
interface HeroStatProps {
  label: string;
  value: string | number;
  subValue?: string;
  variant?: "default" | "positive" | "negative";
  className?: string;
}

export function HeroStat({
  label,
  value,
  subValue,
  variant = "default",
  className,
}: HeroStatProps) {
  const valueColorClass = {
    default: "text-foreground",
    positive: "text-bid-foreground",
    negative: "text-ask-foreground",
  }[variant];

  return (
    <div className={cn("text-center", className)}>
      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
        {label}
      </div>
      <div className={cn("text-3xl font-bold", valueColorClass)}>{value}</div>
      {subValue && (
        <div className="text-xs text-muted-foreground mt-1">{subValue}</div>
      )}
    </div>
  );
}

